// Households (Phase 3): the staff side of the citizens' registration — the Hub operator's in-person verification and
// upkeep, the admins' list and detail, and the import of the eligible list. Every type and path of these APIs lives in
// this one file, aligned with the backend's src/modules/households (households.controller.ts, dto/household.dto.ts,
// household-staff.service.ts › list / detail, household-import.service.ts › ImportReport). The backend's answers
// (`Wire*`) are mapped to the screens' models here, once. NIK, KK, phone and address arrive masked ("•••1234")
// without household.pii.read.
import { HOUSEHOLD_FORM } from '@constants/limits';
import type { UploadMessages, UploadQueueOpts } from '@lib/hooks/useUploadQueue';
import { fetchFile, send, upload, type UploadOpts } from './client';
import { query, type Cursor, type CursorPage } from './paging';

// --- enums (backend common/constants/enums.ts) ----------------------------------------------------------------------

/** PENDING_VERIFICATION → REGISTERED | REJECTED; REGISTERED ↔ SUSPENDED. */
export const HOUSEHOLD_STATUSES = ['PENDING_VERIFICATION', 'REGISTERED', 'REJECTED', 'SUSPENDED'] as const;
export type HouseholdStatus = (typeof HOUSEHOLD_STATUSES)[number];
/** Self-registered in the citizen app, matched to the imported list, or registered at a Hub's counter. */
export const HOUSEHOLD_SOURCES = ['SELF', 'IMPORT', 'COUNTER'] as const;
export type HouseholdSource = (typeof HOUSEHOLD_SOURCES)[number];
export type CitizenRole = 'HEAD' | 'MEMBER';
export type CitizenStatus = 'ACTIVE' | 'BLOCKED';
/** What a staff member may do to a household now (the detail's `allowedActions`; the server checks again). */
/** The backend's household-staff.service.ts › HouseholdStaffAction: what the caller may do now (it checks again). */
export type HouseholdAction = 'verify' | 'reject' | 'suspend' | 'reinstate' | 'memberVerify' | 'memberReject' | 'moveMember' | 'setHead' | 'relocate' | 'phoneChange' | 'transferCylinder' | 'erase' | 'viewKkPhoto';
/** The file kind of the KK photo (through the staff upload route, /hub-uploads). */
export type HouseholdUploadKind = 'HOUSEHOLD_KK';

// --- list and detail ------------------------------------------------------------------------------------------------

/** A row of GET /households (masked without pii.read). */
export interface HouseholdRow {
  id: number;
  status: HouseholdStatus;
  source: HouseholdSource;
  /** the head of the household's name */
  headName: string;
  /** the KK number, masked by key without pii.read (always partly masked in lists) */
  kk: string;
  villageCode: string;
  villageName: string;
  districtName: string;
  regencyName: string;
  provinceCode: string;
  provinceName: string;
  memberCount: number;
  /** members who joined the KK and wait for the Hub's in-person check */
  pendingMembers: number;
  /** cylinders the household holds now (not in the backend's list rows: null there) */
  heldCount: number | null;
  createdAt: string;
  verifiedAt: string | null;
}
export interface HouseholdFilter {
  status?: HouseholdStatus;
  provinceCode?: string;
  regencyCode?: string;
  villageCode?: string;
  source?: HouseholdSource;
  /** a name (a prefix of the head's); a NIK or KK never goes here (POST /households/find) */
  q?: string;
  /** holding a cylinder past DORMANT_DAYS without an exchange */
  dormant?: boolean;
  limit?: number;
}
export type HouseholdPage = CursorPage<HouseholdRow>;
/** Per status in the caller's scope (the tabs' counts): no such answer on the backend yet (null). */
export interface HouseholdSummary { counts: Partial<Record<HouseholdStatus, number>>; total: number; pendingMembers: number; dormant: number | null }

/** A member's place in the household: waiting for the Hub's check (a member joining an existing KK), or accepted. */
export type CitizenMembership = 'NONE' | 'PENDING' | 'ACCEPTED';
export interface HouseholdMember {
  id: number;
  name: string;
  role: CitizenRole;
  status: CitizenStatus;
  membership: CitizenMembership;
  allowedActions: MemberAction[];
  nik: string | null;
  phone: string | null;
  createdAt: string;
}
export interface HeldCylinder { id: number; serialNo: string; since: string | null; orderNumber: string | null; nextTestDue?: string | null }

/** GET /households/:id. */
export interface HouseholdDetail extends HouseholdRow {
  version: number;
  address: string | null;
  lat: number | null;
  lng: number | null;
  /** the head's NIK and phone (masked without pii.read) */
  headNik: string | null;
  headPhone: string | null;
  members: HouseholdMember[];
  /** the Hub whose operator verified it, and who */
  verifiedHubId: number | null;
  verifiedHubName: string | null;
  verifiedByName: string | null;
  verificationNote: string | null;
  rejectionReason: string | null;
  suspensionReason: string | null;
  /** a KK photo was taken at the verification (opened through `kkPhotoUrl`, each view audited) */
  hasKkPhoto: boolean;
  /** matched to the imported list (batch label), when it was */
  eligibleSource: string | null;
  consentVersion: string | null;
  /** this calendar month (WIB): the quota, reserved by open orders, used by completed ones; null: not answered */
  quota: { month: string; limit: number; reserved: number; used: number } | null;
  held: HeldCylinder[];
  /** the latest orders, newest first */
  recentOrders: { id: number; number: string; status: string; placedAt: string }[];
  allowedActions: HouseholdAction[];
}


export const householdPaths = {
  list: (f: HouseholdFilter, cursor: Cursor) =>
    // a text with a digit is never a name: it never reaches a URL (an identity number would be logged on the way)
    `/households${query({ status: f.status, provinceCode: f.provinceCode, regencyCode: f.regencyCode, villageCode: f.villageCode, source: f.source, q: f.q && !/\d/.test(f.q) ? f.q : undefined, dormant: f.dormant ? 'true' : undefined, limit: f.limit, ...cursor })}`,
  detail: (id: number | string) => `/households/${id}`,
  /** GET /households/summary?provinceCode=: counts per state (bounded) and the members waiting, in the caller's area */
  summary: (provinceCode?: string) => `/households/summary${query({ provinceCode })}`,
  /** GET /households/imports?page=&limit=: the import batches */
  imports: (page = 1) => `/households/imports${query({ page, limit: 10 })}`,
  /** GET /households/:id/cylinders/history: the household's cylinders, newest first (cursor = meta.next) */
  cylinderHistory: (id: number | string, cursor: string | null, limit: number) => `/households/${id}/cylinders/history${query({ cursor: cursor ?? undefined, limit })}`,
};

/** One entry of a household's cylinder history: received full, gave an empty back, or a cylinder moved to it. */
export interface HouseholdCylinderEntry {
  id: string;
  kind: 'received' | 'returned_empty' | 'transferred_in';
  at: string;
  cylinder: { id: number; serialNo: string };
  order: { id: number; number: string; channel: 'APP' | 'COUNTER'; fulfilment: 'DELIVERY' | 'PICKUP' } | null;
  hub: { id: number; name: string } | null;
  via: 'COURIER' | 'COUNTER' | null;
  condition: 'GOOD' | 'DAMAGED' | 'MISSING' | null;
  by: { id: number; role: string; name: string } | null;
}
export interface HouseholdCylinderPage { items: HouseholdCylinderEntry[]; meta: { limit: number; next: string | null; prev: null; total: null } }
/** GET /households/:id/kk-photo: the picture itself (household.pii.read; each opening audited), shown from memory. */
/** POST /households/find {nik}|{kk}: an exact identity number, in the body only (0 or 1 household, household.pii.read). */
export interface HouseholdFound { id: number; status: HouseholdStatus; statusLabel: string; villageName: string; headName: string | null }
export const findHousehold = (by: 'nik' | 'kk', value: string) => send<{ items: HouseholdFound[] }>('/households/find', { [by]: value });
export const fetchKkPhoto = async (id: number) => URL.createObjectURL((await fetchFile(`/households/${id}/kk-photo`)).blob);

// --- the Hub operator's in-person verification ------------------------------------------------------------------------

/** POST /hub-uploads?kind=HOUSEHOLD_KK: the KK photo in quarantine until the verification takes it. */
export interface HouseholdUpload { uploadId: string; kind: HouseholdUploadKind; size: number; contentType: string; expiresAt: string }
export const uploadHouseholdFile = (kind: HouseholdUploadKind, body: Blob, opts?: UploadOpts) => upload<HouseholdUpload>(`/hub-uploads${query({ kind })}`, body, opts);

/** The upload queue's options for the KK photo: a picture only, scaled down first. */
export function householdUploadOptions(messages: UploadMessages, formatBytes: (n: number) => string): UploadQueueOpts<HouseholdUploadKind> {
  return {
    send: (kind, body, opts) => uploadHouseholdFile(kind, body, opts),
    types: HOUSEHOLD_FORM.photoTypes,
    rule: () => ({ formats: ['image'], maxBytes: HOUSEHOLD_FORM.maxPhotoBytes }),
    maxPickBytes: HOUSEHOLD_FORM.maxPickBytes,
    scale: HOUSEHOLD_FORM.photo,
    concurrency: 1,
    autoRetries: 3,
    messages,
    formatBytes,
  };
}

const act = (id: number, action: string, body: object) => send<WireHouseholdDetail>(`/households/${id}/${action}`, body).then(toHouseholdDetail);
export const householdActions = {
  /** seen in person: the original KTP and KK, with the KK photo taken at the counter */
  verify: (id: number, version: number, b: { kkPhotoUploadId: string; note?: string }) => act(id, 'verify', { version, ...b }),
  reject: (id: number, version: number, reason: string) => act(id, 'reject', { version, reason }),
  suspend: (id: number, version: number, reason: string) => act(id, 'suspend', { version, reason }),
  reinstate: (id: number, version: number, reason: string) => act(id, 'reinstate', { version, reason }),
  /** a member who joined the KK, checked in person (KTP) at the Hub */
  acceptMember: (id: number, version: number, citizenId: number) => act(id, `members/${citizenId}/verify`, { version }),
  /** the citizen is detached and may register again; the reason (optional) is shown to them in the app */
  rejectMember: (id: number, version: number, citizenId: number, reason?: string) => act(id, `members/${citizenId}/reject`, { version, ...(reason ? { reason } : {}) }),
  /** the head of the family died or left: another accepted member becomes HEAD (history kept) */
  setHead: (id: number, version: number, citizenId: number) => act(id, 'head', { version, citizenId }),
  /** a KK split (pecah KK): the member moves to the household of the new KK (its photo taken), created pending when new */
  moveMember: (id: number, version: number, citizenId: number, b: { kk: string; kkPhotoUploadId: string }) => act(id, `members/${citizenId}/move`, { version, ...b }),
  /** the household moved: a new village and address, checked at the Hub (its Hub follows the village) */
  relocate: (id: number, version: number, b: { villageCode: string; address: string }) => act(id, 'relocate', { version, ...b }),
  /** a held cylinder now with another household (edge case 3): the target found by the counter lookup, the reason kept */
  transferCylinder: (id: number, version: number, cylinderId: number, b: { toHouseholdId: number; reason: string }) => act(id, `cylinders/${cylinderId}/transfer`, { version, ...b }),
};
/** A lost or changed phone, the identity checked at the Hub: a code to the new number, then the change (old sessions end). */
export const citizenActions = {
  phoneOtp: (citizenId: number, phone: string) => send<unknown>(`/citizens/${citizenId}/phone/otp`, { phone }),
  changePhone: (citizenId: number, phone: string, code: string) => send<{ citizenId: number; phone: string }>(`/citizens/${citizenId}/phone`, { phone, code }),
  /** a personal-data deletion request (UU PDP): identity anonymised, history kept; refused while the household holds a cylinder */
  erase: (citizenId: number, reason: string) => send<{ citizenId: number; erased: boolean }>(`/citizens/${citizenId}/erase`, { reason }),
};
/** What a staff member may do to a member (the detail's members[].allowedActions; the server checks again). */
export type MemberAction = 'accept' | 'reject' | 'changePhone' | 'setHead' | 'move' | 'erase';

// --- the imported list of eligible households ------------------------------------------------------------------------

/** The CSV's columns, first line (any case; comma or semicolon) = the backend's IMPORT_COLUMNS. */
export const IMPORT_COLUMNS = ['kk', 'head_nik', 'village_code'] as const;
/** Why the server refused a line (household-import.service.ts › RowProblem). */
export type ImportRowProblem = 'KK_INVALID' | 'NIK_INVALID' | 'VILLAGE_UNKNOWN' | 'DUPLICATE_IN_FILE' | 'COLUMNS' | 'NIK_OTHER_KK' | string;
/** POST /households/import {sourceLabel, csv}: imported at once, the same answer again for the same file. */
export interface ImportReport {
  importId: number;
  sha256: string;
  sourceLabel: string;
  rowsTotal: number;
  rowsImported: number;
  rowsRejected: number;
  alreadyRegistered: number;
  /** pending households registered by this import */
  registered: number;
  /** imported rows without the head's NIK: never registered by the list, the household waits for the Hub's check (absent on a report stored before) */
  needsHubCheck?: number;
  /** the first of those rows: line, the KK's last 4 digits, the backend's note */
  withoutHeadNik?: { line: number; kk: string | null; label: string }[];
  /** the first refused lines: line number (1 = the header), the KK's last 4 digits, why */
  rejected: { line: number; kk: string | null; reason: ImportRowProblem; label: string }[];
  /** the same file again: nothing new imported */
  replay: boolean;
  /** a check only: nothing written */
  dryRun: boolean;
}
/** `dryRun`: the same report, nothing written (the check before the confirmation). */
export const importEligible = (sourceLabel: string, csv: string, dryRun = false) => send<ImportReport>(`/households/import${dryRun ? '?dryRun=true' : ''}`, { sourceLabel, csv });
/** One row of GET /households/imports. */
export interface ImportBatch { id: number; sourceLabel: string; provinceCode: string | null; rowsTotal: number; rowsImported: number; rowsRejected: number; registered: number; importedAt: string; importedBy: { id: number; name: string } | null }
/** GET /households/summary (the counts may be lower bounds: `exact` false means "more than"). */
export interface WireHouseholdSummary { byStatus: Record<HouseholdStatus, { count: number; exact: boolean }>; pendingMembers: { count: number; exact: boolean }; dormant?: { count: number; exact: boolean } }
export const toHouseholdSummary = (w: WireHouseholdSummary): HouseholdSummary => {
  const counts = Object.fromEntries(Object.entries(w.byStatus).map(([k, v]) => [k, v.count])) as Partial<Record<HouseholdStatus, number>>;
  return { counts, total: Object.values(counts).reduce((a, b) => a + (b ?? 0), 0), pendingMembers: w.pendingMembers.count, dormant: w.dormant?.count ?? null };
};

// --- the backend's answers and their mapping (household-staff.service.ts) --------------------------------------------

export interface WireHouseholdRow {
  id: number;
  status: HouseholdStatus;
  source: HouseholdSource;
  kk: string | null;
  villageCode: string;
  villageName: string | null;
  provinceCode: string;
  householdAddress: string;
  headName: string | null;
  headNik: string | null;
  headPhone: string | null;
  members: number;
  pendingMembers: number;
  held: number;
  districtName: string | null;
  regencyName: string | null;
  createdAt: string;
  verifiedAt: string | null;
  version: number;
}
export interface WireMember { citizenId: number; citizenName: string | null; nik: string | null; phone: string | null; role: CitizenRole | null; membership: CitizenMembership; status: CitizenStatus }
export interface WireHouseholdDetail {
  id: number;
  status: HouseholdStatus;
  version: number;
  source: HouseholdSource;
  kk: string | null;
  villageCode: string;
  villageName: string;
  districtName: string;
  regencyName: string;
  provinceCode: string;
  householdAddress: string;
  lat: number | null;
  lng: number | null;
  /** the rejection's or suspension's reason */
  reason: string | null;
  verifiedAt: string | null;
  verifiedBy: { id: number; name: string } | null;
  verifiedHub: { id: number; name: string } | null;
  hasKkPhoto: boolean;
  createdAt: string;
  members: WireMember[];
  held: { cylinderId: number; serialNo: string; status: string; nextTestDue: string | null }[];
  heldCount: number;
  recentOrders: { id: number; number: string; status: string; placedAt: string }[];
  /** this WIB month's quota (absent from an older backend) */
  quota?: { month: string; limit: number; reserved: number; used: number; left: number };
  allowedActions: HouseholdAction[];
}

export const toHouseholdRow = (r: WireHouseholdRow): HouseholdRow => ({
  id: r.id,
  status: r.status,
  source: r.source,
  headName: r.headName ?? '–',
  kk: r.kk ?? '–',
  villageCode: r.villageCode,
  villageName: r.villageName ?? '',
  districtName: r.districtName ?? '',
  regencyName: r.regencyName ?? '',
  provinceCode: r.provinceCode,
  provinceName: '',
  memberCount: r.members,
  pendingMembers: r.pendingMembers,
  heldCount: r.held ?? null,
  createdAt: r.createdAt,
  verifiedAt: r.verifiedAt,
});
export const toHouseholdPage = (p: CursorPage<WireHouseholdRow>): HouseholdPage => ({ items: p.items.map(toHouseholdRow), meta: p.meta });

/** The detail; what the caller may do is the server's `allowedActions` (a member's buttons follow from them). */
export function toHouseholdDetail(d: WireHouseholdDetail): HouseholdDetail {
  const head = d.members.find((m) => m.role === 'HEAD') ?? null;
  const may = new Set(d.allowedActions);
  return {
    ...toHouseholdRow({ ...d, headName: head?.citizenName ?? null, headNik: head?.nik ?? null, headPhone: head?.phone ?? null, members: d.members.length, pendingMembers: d.members.filter((m) => m.membership === 'PENDING').length, held: d.heldCount }),
    version: d.version,
    heldCount: d.heldCount,
    address: d.householdAddress,
    lat: d.lat,
    lng: d.lng,
    headNik: head?.nik ?? null,
    headPhone: head?.phone ?? null,
    members: d.members.map((m) => ({
      id: m.citizenId,
      name: m.citizenName ?? '–',
      role: m.role ?? 'MEMBER',
      status: m.status,
      membership: m.membership,
      nik: m.nik,
      phone: m.phone,
      createdAt: d.createdAt,
      allowedActions: [
        ...(m.membership === 'PENDING' && may.has('memberVerify') ? (['accept'] as const) : []),
        ...(m.membership === 'PENDING' && may.has('memberReject') ? (['reject'] as const) : []),
        ...(m.membership === 'ACCEPTED' && may.has('phoneChange') ? (['changePhone'] as const) : []),
        ...(m.membership === 'ACCEPTED' && may.has('erase') ? (['erase'] as const) : []),
        ...(m.membership === 'ACCEPTED' && m.role !== 'HEAD' && may.has('setHead') ? (['setHead'] as const) : []),
        ...(m.membership === 'ACCEPTED' && m.role !== 'HEAD' && may.has('moveMember') ? (['move'] as const) : []),
      ],
    })),
    verifiedHubId: d.verifiedHub?.id ?? null,
    verifiedHubName: d.verifiedHub?.name ?? null,
    verifiedByName: d.verifiedBy?.name ?? null,
    verificationNote: null,
    rejectionReason: d.status === 'REJECTED' ? d.reason : null,
    suspensionReason: d.status === 'SUSPENDED' ? d.reason : null,
    hasKkPhoto: d.hasKkPhoto,
    eligibleSource: null,
    consentVersion: null,
    quota: d.quota ? { month: d.quota.month, limit: d.quota.limit, reserved: d.quota.reserved, used: d.quota.used } : null,
    held: d.held.map((c) => ({ id: c.cylinderId, serialNo: c.serialNo, since: null, orderNumber: null, nextTestDue: c.nextTestDue })),
    recentOrders: d.recentOrders,
    allowedActions: d.allowedActions,
  };
}

// --- the Hub's counter (walk-in, plan Addendum 2026-11-03) ---------------------------------------------------------

/** Why a household cannot be served now (the lookup's eligibility). */
export type CounterRefusal = 'NOT_REGISTERED' | 'SUSPENDED' | 'QUOTA_EXHAUSTED' | 'HOLDING_LIMIT' | 'ORDER_OPEN_ELSEWHERE' | 'ORDER_ALREADY_OPEN' | 'CANCELLATION_LIMIT' | string;
/** POST /households/lookup {nik}|{kk} (exact, in the body; each lookup audited; 30 a minute): the household and whether the counter may serve it. */
export interface HouseholdLookup {
  household: { id: number; name: string | null; status: HouseholdStatus; statusLabel: string; villageCode: string; villageName: string; address: string | null; kk: string | null; hasPhone: boolean; version: number };
  eligibility: {
    canServe: boolean;
    reasonCode: CounterRefusal | null;
    /** the server's sentence when it cannot be served */
    reason: string | null;
    quotaLeft: number;
    quotaLimit: number;
    heldCount: number;
    heldSerials: string[];
    /** an open order (any channel): at this Hub it is completed through Ambil di Hub; elsewhere it must be cancelled first */
    openOrder: { id: number; number: string; status: string; hubId: number; hubName: string } | null;
    /** what the counter exchange would be: the kind and its bill (no delivery fee), the fines' amounts */
    kind: 'FIRST_ISSUE' | 'EXCHANGE';
    amounts: { lines: { code: 'PRICE' | 'DELIVERY_FEE' | 'DEPOSIT' | string; label: string; amount: number }[]; total: number };
    fineRates: { damaged: number; lost: number } | null;
  };
}
/** The number travels in the body only, never in a URL or web storage. A 404 = no household with it among this Hub's villages. */
/** `purpose`: the counter serving a walk-in, or the target of a cylinder transfer (audited as such). */
export const lookupHousehold = (by: 'nik' | 'kk', value: string, purpose: 'COUNTER' | 'TRANSFER') => send<HouseholdLookup>('/households/lookup', { [by]: value, purpose });
/** The counter bill in the exchange form's terms (no delivery fee at the counter). */
export const counterAmounts = (a: HouseholdLookup['eligibility']['amounts']) => {
  const line = (c: string) => a.lines.find((l) => l.code === c)?.amount ?? 0;
  return { price: line('PRICE'), deliveryFee: 0, deposit: line('DEPOSIT'), fine: 0, total: a.total };
};

/** POST /households/counter-register: registered at once (verified in person by this operator); the phone optional. */
export interface CounterRegisterInput {
  name: string;
  nik: string;
  kk: string;
  villageCode: string;
  address: string;
  phone?: string;
  /** the WhatsApp code the citizen reads out (with `phone` only) */
  phoneCode?: string;
  kkPhotoUploadId: string;
  consentVersion: string;
  /** the consent was read out to the citizen and agreed to */
  consentReadOut: true;
  submitToken: string;
}
export const counterRegister = (b: CounterRegisterInput) => send<{ householdId: number; phone?: string | null }>('/households/counter-register', b);
/** POST /households/counter-register/phone-otp {phone}: a code to the citizen's WhatsApp while it is at the counter (rate limited). */
export const counterPhoneOtp = (phone: string) => send<{ expiresAt: string; resendAt: string }>('/households/counter-register/phone-otp', { phone });
/** GET /citizen/consent (open): the consent text and its version, read out at the counter. */
export interface HouseholdConsent { version: string; text: string }
export const householdConsentPath = '/citizen/consent';
